import React, { useCallback, useEffect, useRef, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../utils/firebaseConfig";
import OrnamentalDivider from "./OrnamentalDivider";
import BookNowCTA from "./BookNowCTA";
import "../Styles/SignatureDishes.css";

const MAX_DISHES = 10;

/**
 * Splits a comma / newline separated string into trimmed, non-empty items.
 * Used for the "key ingredients" and "dietary / allergens" chip lists.
 */
const toList = (value) =>
  (value || "")
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);

/**
 * Splits a free-text story into paragraphs on blank lines (or single newlines),
 * so admins can write multi-paragraph descriptions naturally.
 */
const toParagraphs = (value) =>
  (value || "")
    .split(/\n\s*\n|\n/)
    .map((p) => p.trim())
    .filter(Boolean);

/**
 * Sort by the optional numeric `order` field (ascending), falling back to
 * creation time so dishes appear in a stable, admin-controllable order.
 */
const byOrder = (a, b) => {
  const ao = typeof a.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
  const bo = typeof b.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  const at = a.createdAt?.seconds ?? 0;
  const bt = b.createdAt?.seconds ?? 0;
  return at - bt;
};

const SignatureDishes = () => {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDish, setActiveDish] = useState(null);
  const closeButtonRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    const fetchDishes = async () => {
      try {
        const snap = await getDocs(collection(db, "signatureDishes"));
        const docs = [];
        snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
        docs.sort(byOrder);
        setDishes(docs.slice(0, MAX_DISHES));
      } catch (err) {
        console.log("Error loading signature dishes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDishes();
  }, []);

  const closeModal = useCallback(() => setActiveDish(null), []);

  const openModal = (dish, event) => {
    lastFocusedRef.current = event?.currentTarget || null;
    setActiveDish(dish);
  };

  // Close on Escape and lock body scroll while the article modal is open.
  useEffect(() => {
    if (!activeDish) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Move focus into the modal for keyboard / screen-reader users.
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      // Restore focus to the card that opened the modal.
      lastFocusedRef.current?.focus?.();
    };
  }, [activeDish, closeModal]);

  // Hide the whole section while loading or when there is nothing to feature.
  if (loading) return null;
  if (!dishes.length) return null;

  return (
    <section
      className="signature-dishes"
      aria-labelledby="signature-dishes-title"
    >
      <header className="signature-dishes__intro">
        <span className="md-eyebrow">From our kitchen</span>
        <h2 id="signature-dishes-title" className="signature-dishes__title">
          Our Signature Dishes
        </h2>
        <span className="signature-dishes__subtitle">
          the plates we are known for
        </span>
      </header>

      <OrnamentalDivider />

      <ul className="signature-dishes__grid">
        {dishes.map((dish) => (
          <li key={dish.id} className="signature-dishes__grid-item">
            <article
              className="dish-card"
              role="button"
              tabIndex={0}
              aria-haspopup="dialog"
              onClick={(e) => openModal(dish, e)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openModal(dish, e);
                }
              }}
            >
              <div className="dish-card__image">
                {dish.image ? (
                  <img src={dish.image} alt={dish.name || ""} loading="lazy" />
                ) : (
                  <div className="dish-card__image-placeholder" aria-hidden="true" />
                )}
                {dish.price && (
                  <span className="dish-card__price">{dish.price}</span>
                )}
              </div>
              <div className="dish-card__body">
                <h3 className="dish-card__name">{dish.name}</h3>
                {dish.tagline && (
                  <p className="dish-card__tagline">{dish.tagline}</p>
                )}
                <span className="dish-card__cta">View dish</span>
              </div>
            </article>
          </li>
        ))}
      </ul>

      <BookNowCTA variant="inline" label="Reserve your table" />

      {activeDish && (
        <DishModal
          dish={activeDish}
          onClose={closeModal}
          closeButtonRef={closeButtonRef}
        />
      )}
    </section>
  );
};

const DishModal = ({ dish, onClose, closeButtonRef }) => {
  const ingredients = toList(dish.ingredients);
  const dietary = toList(dish.dietary);
  const paragraphs = toParagraphs(dish.story);
  const hasDetails =
    ingredients.length || dish.price || dietary.length || dish.pairing;

  return (
    <div
      className="dish-modal__overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="dish-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dish-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="dish-modal__close"
          onClick={onClose}
          aria-label="Close"
          ref={closeButtonRef}
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <div className="dish-modal__hero">
          {dish.image ? (
            <img src={dish.image} alt={dish.name || ""} />
          ) : (
            <div className="dish-modal__hero-placeholder" aria-hidden="true" />
          )}
        </div>

        <div className="dish-modal__content">
          <span className="md-eyebrow">Signature Dish</span>
          <h2 id="dish-modal-title" className="dish-modal__title">
            {dish.name}
          </h2>
          {dish.tagline && (
            <p className="dish-modal__tagline">{dish.tagline}</p>
          )}

          {paragraphs.length > 0 && (
            <div className="dish-modal__story">
              {paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          )}

          {hasDetails ? (
            <dl className="dish-modal__details">
              {ingredients.length > 0 && (
                <div className="dish-modal__detail">
                  <dt>Key ingredients</dt>
                  <dd>
                    <ul className="dish-chips">
                      {ingredients.map((item, i) => (
                        <li key={i} className="dish-chip">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </dd>
                </div>
              )}

              {dish.price && (
                <div className="dish-modal__detail">
                  <dt>Price</dt>
                  <dd className="dish-modal__price">{dish.price}</dd>
                </div>
              )}

              {dietary.length > 0 && (
                <div className="dish-modal__detail">
                  <dt>Dietary &amp; allergens</dt>
                  <dd>
                    <ul className="dish-chips">
                      {dietary.map((item, i) => (
                        <li key={i} className="dish-chip dish-chip--diet">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </dd>
                </div>
              )}

              {dish.pairing && (
                <div className="dish-modal__detail">
                  <dt>Pairs well with</dt>
                  <dd>{dish.pairing}</dd>
                </div>
              )}
            </dl>
          ) : null}

          {dish.chefNote && (
            <blockquote className="dish-modal__chef-note">
              <span className="dish-modal__chef-label">Chef&rsquo;s note</span>
              {dish.chefNote}
            </blockquote>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignatureDishes;
